import type {
  HilPresentationReplyEnvelope,
  HilPresentationReplyResponder,
  HilPresentationReplyAction,
  HilReplyStep,
} from '@bizfirst/hil-types';
import type { HilDecisionValue } from '@bizfirst/hil-types';
import type { IHilSubmitClient, HilSubmitResult } from './IHilSubmitClient';
import { FetchHilSubmitClient } from './FetchHilSubmitClient';

export interface HilWriterParams {
  engageSessionID: number;
  correlationID: string;
  resumeCallbackURL: string;
  processElementID?: string;
  replyToID?: string;
  submitClient?: IHilSubmitClient;

  // No-Engage resume path (e.g. Octopus AI Agent chat, where engageSessionID is always 0). Both
  // additive/optional — absent for every session that has a real engageSessionID, which continues
  // resuming exactly as before.
  executionResID?: string;
  nodeKey?: string;
}

export class HilPresentationReplyEnvelopeWriter {
  private readonly engageSessionID: number;
  private readonly correlationID: string;
  private readonly resumeCallbackURL: string;
  private readonly processElementID: string | undefined;
  private readonly replyToID: string | undefined;
  private readonly submitClient: IHilSubmitClient;
  private readonly executionResID: string | undefined;
  private readonly nodeKey: string | undefined;

  private _decision: string = '';
  private _actionName: string | undefined;
  private _channel: string = 'web';
  private _decidedAt: string | undefined;
  private _responders: HilPresentationReplyResponder[] = [];
  private _auditActions: HilPresentationReplyAction[] = [];
  private _dataSteps: HilReplyStep[] = [];
  private _replyText: string | undefined;
  private _comment: string | undefined;
  private _reason: string | undefined;
  private _metadata: Record<string, unknown> = {};

  constructor(params: HilWriterParams) {
    this.engageSessionID = params.engageSessionID;
    this.correlationID = params.correlationID;
    this.resumeCallbackURL = params.resumeCallbackURL;
    this.processElementID = params.processElementID;
    this.replyToID = params.replyToID;
    this.submitClient = params.submitClient ?? new FetchHilSubmitClient();
    this.executionResID = params.executionResID;
    this.nodeKey = params.nodeKey;
  }

  // ── Identity accessors ────────────────────────────────────────────────────────
  // Public getters for the two fields callers need to resolve the exact turn this writer's
  // reply belongs to (see useHilSubmit.ts). EngageSessionID alone is not a safe match key for
  // the no-Engage resume path (always 0 across every turn of the same chat) — CorrelationID is
  // unique per turn and must travel alongside it.
  getEngageSessionID(): number {
    return this.engageSessionID;
  }

  getCorrelationID(): string {
    return this.correlationID;
  }

  // ── Decision ─────────────────────────────────────────────────────────────────
  setDecision(decision: HilDecisionValue | string): this {
    this._decision = decision;
    return this;
  }

  setActionName(actionName: string): this {
    this._actionName = actionName;
    return this;
  }

  setChannel(channel: string): this {
    this._channel = channel;
    return this;
  }

  setDecidedAt(isoTimestamp?: string): this {
    this._decidedAt = isoTimestamp ?? new Date().toISOString();
    return this;
  }

  // ── Responders ────────────────────────────────────────────────────────────────
  /**
   * Root-caused 2026-09-12 (ChatDesk defect: every reply 400'd with "The JSON value could not be
   * converted to System.String" at $.Responders[0].ActorID): the C# wire contract
   * (`HilPresentationReplyResponder.ActorID`) is `string`, but at least one real upstream identity
   * source in this codebase (`@bizfirst/common-auth-react`'s `SsoAuthGate` -> `useSsoHandoffSession`
   * -> `id: profile.userID`) hands callers a value that is typed `string` at the TS level while
   * genuinely a JS `number` at runtime (the backend's numeric UserID, never coerced) — so
   * `JSON.stringify` emitted an unquoted number and System.Text.Json's strict deserializer rejected
   * it outright before this envelope's own action method ever ran (zero app-level logs, pure
   * ASP.NET model-validation 400). `String(actorID)` here is the one place every caller's reply
   * envelope passes through before hitting the wire, so coercing at this boundary protects every
   * current and future `setCurrentUserAsResponder`/`addResponder` caller from the same upstream
   * type lie, rather than requiring each one to remember to coerce its own identity value.
   */
  setCurrentUserAsResponder(actorID: string, displayName?: string): this {
    const id = String(actorID);
    this._responders = this._responders.filter(r => r.ActorID !== id);
    this._responders.unshift({ ActorID: id, DisplayName: displayName != null ? String(displayName) : undefined, ResponderRole: 'primary' });
    return this;
  }

  addResponder(params: {
    actorID: string;
    actorType?: string;
    displayName?: string;
    responderRole?: string;
  }): this {
    this._responders.push({
      ActorID: String(params.actorID),
      ActorType: params.actorType,
      DisplayName: params.displayName != null ? String(params.displayName) : undefined,
      ResponderRole: params.responderRole ?? 'secondary',
    });
    return this;
  }

  // ── Step data ─────────────────────────────────────────────────────────────────
  addFormStep(params: {
    stepOrder: number;
    data: Record<string, unknown>;
    actionName?: string;
    completedAt?: string;
  }): this {
    this._dataSteps.push({
      StepOrder: params.stepOrder,
      PresentationType: 'form',
      ActionName: params.actionName,
      Data: params.data,
      CompletedAt: params.completedAt ?? new Date().toISOString(),
    });
    return this;
  }

  addChatStep(params: {
    stepOrder: number;
    replyText: string;
    actionName?: string;
  }): this {
    this._dataSteps.push({
      StepOrder: params.stepOrder,
      PresentationType: 'chat',
      ActionName: params.actionName,
      ReplyText: params.replyText,
      CompletedAt: new Date().toISOString(),
    });
    return this;
  }

  addApprovalStep(params: {
    stepOrder: number;
    comment?: string;
    reason?: string;
    actionName?: string;
  }): this {
    this._dataSteps.push({
      StepOrder: params.stepOrder,
      PresentationType: 'approval',
      ActionName: params.actionName,
      Data: {
        ...(params.comment ? { comment: params.comment } : {}),
        ...(params.reason ? { reason: params.reason } : {}),
      },
      CompletedAt: new Date().toISOString(),
    });
    return this;
  }

  // ── Payload convenience setters ───────────────────────────────────────────────
  setReplyText(replyText: string): this {
    this._replyText = replyText;
    return this;
  }

  setComment(comment: string): this {
    this._comment = comment;
    return this;
  }

  setReason(reason: string): this {
    this._reason = reason;
    return this;
  }

  // ── Audit trail ───────────────────────────────────────────────────────────────
  addAuditAction(params: {
    actionType: string;
    actionName?: string;
    actionLabel?: string;
    performedBy: string;
    performedAt?: string;
    actionData?: Record<string, unknown>;
  }): this {
    this._auditActions.push({
      ActionType: params.actionType,
      ActionName: params.actionName,
      ActionLabel: params.actionLabel,
      PerformedBy: params.performedBy,
      PerformedAt: params.performedAt ?? new Date().toISOString(),
      ActionData: params.actionData,
    });
    return this;
  }

  // ── Metadata ──────────────────────────────────────────────────────────────────
  addMetadata(key: string, value: unknown): this {
    this._metadata[key] = value;
    return this;
  }

  // ── Output ────────────────────────────────────────────────────────────────────
  /**
   * Resolves the top-level Payload.ReplyText the backend's
   * HilOutputPortRouterService.ExtractReplyTextFromDecision actually reads. An explicit
   * setReplyText() call always wins; otherwise falls back to the most recently added chat
   * DataStep's ReplyText, so a caller that only ever calls addChatStep() (as
   * HilChatComposer's Send button does — it never calls setReplyText itself) still produces a
   * populated top-level field instead of silently submitting an empty reply. Mirrors build()'s
   * existing DataSteps → Payload.Data merge precedent below.
   */
  private resolveReplyText(): string | undefined {
    if (this._replyText !== undefined) return this._replyText;
    for (let i = this._dataSteps.length - 1; i >= 0; i--) {
      const step = this._dataSteps[i];
      if (step.PresentationType === 'chat' && step.ReplyText !== undefined) return step.ReplyText;
    }
    return undefined;
  }

  get data(): HilPresentationReplyEnvelope {
    return {
      Version: '1.3',
      EngageSessionID: this.engageSessionID,
      ExecutionResID: this.executionResID,
      NodeKey: this.nodeKey,
      CorrelationID: this.correlationID,
      ReplyToID: this.replyToID,
      Decision: this._decision,
      ActionName: this._actionName,
      DecidedAt: this._decidedAt ?? new Date().toISOString(),
      Channel: this._channel,
      Responders: [...this._responders],
      Actions: this._auditActions.length > 0 ? [...this._auditActions] : undefined,
      Payload: {
        DataSteps: this._dataSteps.length > 0 ? [...this._dataSteps] : undefined,
        Data: undefined,
        ReplyText: this.resolveReplyText(),
        Comment: this._comment,
        Reason: this._reason,
      },
      Metadata: Object.keys(this._metadata).length > 0 ? { ...this._metadata } : undefined,
    };
  }

  validate(): { ok: boolean; errors: string[] } {
    const errors: string[] = [];
    if (!this._decision) errors.push('Decision is required');
    if (this._responders.length === 0) errors.push('At least one Responder is required');
    return { ok: errors.length === 0, errors };
  }

  build(): HilPresentationReplyEnvelope {
    const validation = this.validate();
    if (!validation.ok) throw new Error(`HilWriter validation failed: ${validation.errors.join('; ')}`);

    const mergedData: Record<string, unknown> = {};
    for (const step of this._dataSteps) {
      if (step.Data) Object.assign(mergedData, step.Data);
    }

    const envelope = this.data;
    envelope.Payload.Data = Object.keys(mergedData).length > 0 ? mergedData : undefined;
    return envelope;
  }

  // ── Submission ────────────────────────────────────────────────────────────────
  resolveSubmitURL(): string {
    if (this.resumeCallbackURL && this.resumeCallbackURL.startsWith('http')) {
      return this.resumeCallbackURL;
    }
    if (this.processElementID) {
      return `/Nodes/${this.processElementID}/`;
    }
    return this.resumeCallbackURL;
  }

  async submit(overrideURL?: string): Promise<HilSubmitResult> {
    const envelope = this.build();
    const url = overrideURL ?? this.resolveSubmitURL();
    return this.submitClient.post(url, envelope);
  }
}
